import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { executeRelease } from "../api/releasesApi";
import { fetchAllLiveOrgs } from "../api/usersApi";
import "./ExecuteReleaseModal.css";

/**
 * ExecuteReleaseModal
 * Calls API 5: POST /api/releases/{id}/execute
 *
 * Always operates in CLIENT mode:
 *   Shows ALL features in the release (from features prop, loaded via API 11).
 *   For each feature, user picks which clients should receive it.
 *   Features can be toggled on/off (skip features the user doesn't want to release now).
 *   Payload: { clientMap: { FEATURE_ID: ["clientId1", "clientId2", …] } } — backend expects client IDs only.
 *
 * @param {boolean}  show
 * @param {function} onHide
 * @param {object}   release   — must be status=READY
 * @param {Array}    features  — all features in this release (from API 11)
 * @param {function} onSuccess — called with API response
 */
const ExecuteReleaseModal = ({ show, onHide, release, features: featuresProp, onSuccess }) => {

    /* ── Live clients from API (each has clientId for payload; displayName for UI) ── */
    const [liveClients, setLiveClients] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [orgsError, setOrgsError] = useState("");

    /* ── Feature rows — one per feature in the release ────────── */
    // Each row: { featureId, featureName, included: boolean, selectedClientIds: Set<string> } — store client IDs for API
    const [featureRows, setFeatureRows] = useState([]);

    /* ── Per-row client search filter ─────────────────────────── */
    const [clientSearch, setClientSearch] = useState({});

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [confirmed, setConfirmed] = useState(false);

    /* ── Safely normalize features from prop ──────────────────── */
    const safeFeatures = useMemo(() => {
        if (!Array.isArray(featuresProp)) return [];
        return featuresProp;
    }, [featuresProp]);

    /* ── Track the feature-list content to avoid re-init on ref changes ── */
    const initializedForRef = useRef(null);

    /* ── Build feature rows when modal opens or release truly changes ─── */
    useEffect(() => {
        if (!show) {
            // Reset the guard when modal closes so next open re-initialises
            initializedForRef.current = null;
            return;
        }

        // Derive a stable content key from the feature IDs in this release
        const contentKey = safeFeatures.map((f) => f.featureId).join(",");
        if (initializedForRef.current === contentKey) return; // same content — skip
        initializedForRef.current = contentKey;

        // Reset all state cleanly
        setSubmitting(false);
        setError("");
        setConfirmed(false);
        setClientSearch({});

        // Build one row per feature from the release
        if (safeFeatures.length > 0) {
            setFeatureRows(
                safeFeatures.map((f) => ({
                    featureId: f.featureId || "",
                    featureName: f.featureName || f.featureId || "",
                    version: f.version || "",
                    releaseType: f.releaseType || "CLIENT",
                    included: true,        // all features included by default
                    selectedClientIds: new Set(),
                }))
            );
        } else {
            setFeatureRows([]);
        }
    }, [show, safeFeatures]);

    /* ── Pre-load live clients whenever the modal opens ────────────────── */
    useEffect(() => {
        if (!show) return;
        setOrgsLoading(true);
        setOrgsError("");
        fetchAllLiveOrgs()
            .then(setLiveClients)
            .catch(() =>
                setOrgsError("Could not load live clients. Check your connection.")
            )
            .finally(() => setOrgsLoading(false));
    }, [show]);

    /* ── Toggle feature included/excluded ─────────────────────── */
    const toggleIncluded = (featureId) =>
        setFeatureRows((prev) =>
            prev.map((row) =>
                row.featureId === featureId
                    ? { ...row, included: !row.included }
                    : row
            )
        );

    /* ── Client selection helpers (by clientId for API; show names in UI) ── */
    const toggleClient = (featureId, clientId) =>
        setFeatureRows((prev) =>
            prev.map((row) => {
                if (row.featureId !== featureId) return row;
                const next = new Set(row.selectedClientIds);
                next.has(clientId) ? next.delete(clientId) : next.add(clientId);
                return { ...row, selectedClientIds: next };
            })
        );

    const toggleAll = (featureId, checked, clientIds) =>
        setFeatureRows((prev) =>
            prev.map((row) =>
                row.featureId === featureId
                    ? {
                        ...row,
                        selectedClientIds: checked
                            ? new Set(clientIds)
                            : new Set(),
                    }
                    : row
            )
        );

    const filteredClients = (featureId) => {
        const q = (clientSearch[featureId] || "").toLowerCase();
        if (!q) return liveClients;
        return liveClients.filter(
            (c) =>
                (c.clientId || "").toLowerCase().includes(q) ||
                (c.org || "").toLowerCase().includes(q) ||
                (c.displayName || "").toLowerCase().includes(q) ||
                (c.email || "").toLowerCase().includes(q)
        );
    };

    /* ── Derived data ─────────────────────────────────────────── */
    const includedRows = useMemo(
        () => featureRows.filter((r) => r.included),
        [featureRows]
    );

    const totalClientsSelected = useMemo(
        () => includedRows.reduce((sum, row) => sum + row.selectedClientIds.size, 0),
        [includedRows]
    );

    /* ── Validate ─────────────────────────────────────────────── */
    const validate = () => {
        if (includedRows.length === 0) {
            setError("Please include at least one feature to release.");
            return false;
        }
        const unassigned = includedRows.filter((r) => r.selectedClientIds.size === 0);
        if (unassigned.length > 0) {
            setError(
                `Please select at least 1 client for: ${unassigned
                    .map((r) => r.featureName || r.featureId)
                    .join(", ")}`
            );
            return false;
        }
        return true;
    };

    /* ── Build payload & submit (send client IDs only to backend) ── */
    const handleSubmit = async () => {
        if (!confirmed) return;
        if (!validate()) return;

        setSubmitting(true);
        setError("");

        const clientMap = {};
        for (const row of includedRows) {
            if (row.selectedClientIds.size > 0) {
                clientMap[row.featureId] = Array.from(row.selectedClientIds);
            }
        }

        try {
            const result = await executeRelease(release.releaseId, clientMap);
            onSuccess?.(result);
            onHide();
        } catch (err) {
            setError(err?.message || "Execution failed. Please try again.");
            setSubmitting(false);
        }
    };

    const canSubmit = confirmed && !submitting;

    /* ── Render ───────────────────────────────────────────────── */
    return (
        <Modal
            show={show}
            onHide={onHide}
            className="execute-release-modal"
            centered
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="erm-title-row">
                        <span className="erm-title-icon" aria-hidden="true">🚀</span>
                        Execute Release
                    </div>
                    <p className="erm-title-sub">{release?.releaseName || "This Release"}</p>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Warning banner */}
                <div className="erm-warning-banner">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 22h16a2 2 0 0 0 1.73-4Z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>
                        <strong>Irreversible action.</strong> Once executed, this release moves to{" "}
                        <strong>RELEASED</strong> status and cannot be undone.
                    </span>
                </div>

                {error && (
                    <div className="erm-error-banner" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {error}
                    </div>
                )}

                {/* ── No features fallback ─────────────────────────── */}
                {safeFeatures.length === 0 && (
                    <div className="erm-no-features">
                        <p>
                            <strong>No features found in this release.</strong>
                        </p>
                        <p>
                            Please add at least one feature before executing. Go back and use
                            the "Add Feature" button.
                        </p>
                    </div>
                )}

                {/* ── Features & client assignment ─────────────────── */}
                {safeFeatures.length > 0 && (
                    <div className="erm-section">
                        <p className="erm-section__label">
                            Features in this release
                            <span className="erm-section__badge">{featureRows.length}</span>
                        </p>
                        <p className="erm-section__hint">
                            Toggle features on/off and select which clients receive each feature.
                        </p>

                        <div className="erm-features-list">
                            {featureRows.map((row) => {
                                const filtered = filteredClients(row.featureId);
                                const allChecked =
                                    filtered.length > 0 &&
                                    filtered.every((c) => row.selectedClientIds.has(c.clientId));

                                return (
                                    <div
                                        key={row.featureId}
                                        className={`erm-feature-row erm-feature-row--client ${!row.included ? "erm-feature-row--excluded" : ""}`}
                                    >
                                        {/* Feature header */}
                                        <div className="erm-feature-row__top">
                                            <div className="erm-feature-row__info">
                                                {/* Include toggle */}
                                                <label className="erm-feature-toggle" title={row.included ? "Included — click to skip" : "Excluded — click to include"}>
                                                    <input
                                                        type="checkbox"
                                                        checked={row.included}
                                                        onChange={() => toggleIncluded(row.featureId)}
                                                        className="erm-feature-toggle__input"
                                                    />
                                                    <span className="erm-feature-toggle__slider" />
                                                </label>
                                                <span className="erm-feature-row__name">
                                                    {row.featureName}
                                                </span>
                                                <code className="erm-feature-row__id">
                                                    {row.featureId}
                                                </code>
                                            </div>
                                            <div className="erm-feature-row__right">
                                                {row.included && row.selectedClientIds.size > 0 && (
                                                    <span className="erm-feature-row__count">
                                                        {row.selectedClientIds.size} client
                                                        {row.selectedClientIds.size !== 1 ? "s" : ""} selected
                                                    </span>
                                                )}
                                                {!row.included && (
                                                    <span className="erm-feature-row__skipped">
                                                        Skipped
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Client picker — only if feature is included */}
                                        {row.included && (
                                            <div className="erm-client-picker">
                                                {orgsLoading ? (
                                                    <div className="erm-client-picker__loading">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                            stroke="currentColor" strokeWidth="2"
                                                            strokeLinecap="round" strokeLinejoin="round"
                                                            style={{ animation: "erm-spin 1s linear infinite" }}>
                                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                                        </svg>
                                                        Loading live clients…
                                                    </div>
                                                ) : orgsError ? (
                                                    <div className="erm-client-picker__error">{orgsError}</div>
                                                ) : liveClients.length === 0 ? (
                                                    <div className="erm-client-picker__empty">
                                                        No live clients found.
                                                    </div>
                                                ) : (
                                                    <>
                                                        {/* Search */}
                                                        <div className="erm-client-picker__search-wrap">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                                stroke="currentColor" strokeWidth="2"
                                                                strokeLinecap="round" strokeLinejoin="round"
                                                                aria-hidden="true">
                                                                <circle cx="11" cy="11" r="8" />
                                                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                            </svg>
                                                            <input
                                                                type="text"
                                                                className="erm-client-picker__search"
                                                                placeholder="Search by name, org ID, or email…"
                                                                aria-label="Search clients by name, org ID, or email"
                                                                value={clientSearch[row.featureId] || ""}
                                                                onChange={(e) =>
                                                                    setClientSearch((prev) => ({
                                                                        ...prev,
                                                                        [row.featureId]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </div>

                                                        {/* Select all — operates on the current filtered list */}
                                                        <label className="erm-client-picker__select-all">
                                                            <input
                                                                type="checkbox"
                                                                checked={allChecked}
                                                                onChange={(e) =>
                                                                    toggleAll(
                                                                        row.featureId,
                                                                        e.target.checked,
                                                                        filtered.map((c) => c.clientId)
                                                                    )
                                                                }
                                                            />
                                                            <span>
                                                                Select all ({filtered.length} client{filtered.length !== 1 ? "s" : ""})
                                                            </span>
                                                        </label>

                                                        {/* Client list */}
                                                        <div className="erm-client-picker__list">
                                                            {filtered.length === 0 ? (
                                                                <div className="erm-client-picker__empty">
                                                                    No clients match your search.
                                                                </div>
                                                            ) : (
                                                                filtered.map((c) => (
                                                                    <label
                                                                        key={c.clientId}
                                                                        className={`erm-client-item ${row.selectedClientIds.has(c.clientId)
                                                                            ? "erm-client-item--checked"
                                                                            : ""
                                                                            }`}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={row.selectedClientIds.has(c.clientId)}
                                                                            onChange={() =>
                                                                                toggleClient(row.featureId, c.clientId)
                                                                            }
                                                                            className="erm-client-item__check"
                                                                        />
                                                                        <div className="erm-client-item__body">
                                                                            <span className="erm-client-item__name">
                                                                                {c.displayName || c.org || c.clientId}
                                                                            </span>
                                                                            <span className="erm-client-item__meta">
                                                                                <code className="erm-client-item__org">
                                                                                    {c.org}
                                                                                </code>
                                                                                {c.email && (
                                                                                    <span className="erm-client-item__email">
                                                                                        · {c.email}
                                                                                    </span>
                                                                                )}
                                                                                {c.planType && (
                                                                                    <span className="erm-client-item__plan">
                                                                                        {c.planType}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </label>
                                                                ))
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── Summary ─────────────────────────────────────── */}
                {includedRows.length > 0 && totalClientsSelected > 0 && (
                    <div className="erm-summary">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span>
                            <strong>{totalClientsSelected}</strong> total client assignment
                            {totalClientsSelected !== 1 ? "s" : ""} across{" "}
                            <strong>{includedRows.length}</strong>{" "}
                            feature{includedRows.length !== 1 ? "s" : ""}.
                            {featureRows.length > includedRows.length && (
                                <> ({featureRows.length - includedRows.length} feature
                                    {featureRows.length - includedRows.length !== 1 ? "s" : ""} skipped)</>
                            )}
                        </span>
                    </div>
                )}

                {/* ── Confirmation checkbox ─────────────────────────── */}
                <div className="erm-confirm">
                    <Form.Check
                        type="checkbox"
                        id="execute-confirm-checkbox"
                        label={
                            <span className="erm-confirm__label">
                                I understand this action is <strong>irreversible</strong> and will push{" "}
                                <strong>{release?.releaseName}</strong> to live clients.
                            </span>
                        }
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="erm-confirm__check"
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="warning"
                    className="erm-btn-execute"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {submitting ? (
                        <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round"
                                aria-hidden="true"
                                style={{ animation: "erm-spin 1s linear infinite" }}>
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Executing…
                        </>
                    ) : (
                        <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                            Execute Release
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExecuteReleaseModal;
